/**
 * Leviathan Web - Driver Detail Page
 * Quasar Prime LLC
 * View and edit individual driver details
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import DriverForm from '@components/drivers/DriverForm';
import DriverTerminalCredentials from '@components/drivers/DriverTerminalCredentials';
import { API_URL } from '@/config/api';

interface Driver {
  id: string;
  driverNumber: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  employeeId: string | null;
  hireDate: string | null;
  terminationDate: string | null;
  employmentStatus: string;
  licenseNumber: string | null;
  licenseState: string | null;
  licenseClass: string | null;
  licenseExpiry: string | null;
  hazmatCertified: boolean;
  hazmatExpiry: string | null;
  tankerEndorsement: boolean;
  twicCard: boolean;
  twicExpiry: string | null;
  medicalCardExpiry: string | null;
  status: string;
  currentTruckId: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Format date for display
 */
function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format date for form input (YYYY-MM-DD)
 */
function formatDateForInput(dateStr: string | null): string {
  if (!dateStr) return '';
  return new Date(dateStr).toISOString().split('T')[0] ?? '';
}

/**
 * Check if date is within 30 days or past
 */
function isDateWarning(dateStr: string | null): 'critical' | 'warning' | null {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const today = new Date();
  const thirtyDays = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);

  if (date < today) return 'critical';
  if (date <= thirtyDays) return 'warning';
  return null;
}

/**
 * Get status styling
 */
function getStatusStyle(status: string): string {
  switch (status) {
    case 'AVAILABLE':
      return 'text-success-green bg-success-green/10';
    case 'ON_DUTY':
      return 'text-amber bg-amber/10';
    case 'OFF_DUTY':
      return 'text-text-tertiary bg-text-tertiary/10';
    case 'VACATION':
      return 'text-steel bg-steel/10';
    case 'SICK':
      return 'text-warning-amber bg-warning-amber/10';
    case 'SUSPENDED':
      return 'text-error-red bg-error-red/10';
    default:
      return 'text-text-secondary bg-text-secondary/10';
  }
}

/**
 * Get employment status styling
 */
function getEmploymentStyle(status: string): string {
  switch (status) {
    case 'ACTIVE':
      return 'text-success-green';
    case 'ON_LEAVE':
      return 'text-warning-amber';
    case 'TERMINATED':
    case 'RETIRED':
      return 'text-text-tertiary';
    default:
      return 'text-text-secondary';
  }
}

const DriverDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [driver, setDriver] = useState<Driver | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  useEffect(() => {
    fetchDriver();
  }, [id]);

  const fetchDriver = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/drivers/${id}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setDriver(data.driver);
      } else if (response.status === 404) {
        navigate('/drivers');
      }
    } catch (error) {
      console.error('Failed to fetch driver:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) return;

    try {
      const response = await fetch(`${API_URL}/api/drivers/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        setShowStatusModal(false);
        setNewStatus('');
        fetchDriver();
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/api/drivers/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        navigate('/drivers');
      }
    } catch (error) {
      console.error('Failed to delete driver:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="p-12 text-center">
        <div className="text-text-tertiary font-mono animate-pulse">
          &gt; Loading driver data...
        </div>
      </div>
    );
  }

  if (!driver) {
    return (
      <div className="p-12 text-center">
        <div className="text-text-tertiary font-mono">&gt; Driver not found</div>
      </div>
    );
  }

  // Prepare initial data for edit form
  const initialFormData = {
    driverNumber: driver.driverNumber,
    firstName: driver.firstName,
    lastName: driver.lastName,
    email: driver.email || '',
    phone: driver.phone || '',
    address: driver.address || '',
    city: driver.city || '',
    state: driver.state || '',
    zip: driver.zip || '',
    employeeId: driver.employeeId || '',
    hireDate: formatDateForInput(driver.hireDate),
    employmentStatus: driver.employmentStatus as 'ACTIVE' | 'ON_LEAVE' | 'TERMINATED' | 'RETIRED',
    licenseNumber: driver.licenseNumber || '',
    licenseState: driver.licenseState || '',
    licenseClass: driver.licenseClass || '',
    licenseExpiry: formatDateForInput(driver.licenseExpiry),
    hazmatCertified: driver.hazmatCertified,
    hazmatExpiry: formatDateForInput(driver.hazmatExpiry),
    tankerEndorsement: driver.tankerEndorsement,
    twicCard: driver.twicCard,
    twicExpiry: formatDateForInput(driver.twicExpiry),
    medicalCardExpiry: formatDateForInput(driver.medicalCardExpiry),
    status: driver.status as 'AVAILABLE' | 'ON_DUTY' | 'OFF_DUTY' | 'VACATION' | 'SICK' | 'SUSPENDED',
    notes: driver.notes || '',
  };

  if (isEditMode) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="relative">
          <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
          <div className="border-l-4 border-amber pl-6 relative">
            <div className="flex items-center justify-between">
              <div>
                <h1
                  className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                  style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
                >
                  Edit {driver.firstName} {driver.lastName}
                </h1>
                <button
                  onClick={() => setIsEditMode(false)}
                  className="text-text-secondary text-sm font-mono hover:text-amber transition-colors mt-2"
                >
                  &larr; Cancel Edit
                </button>
              </div>
            </div>
          </div>
        </div>

        <DriverForm
          driverId={id}
          initialData={initialFormData}
          onSuccess={() => {
            setIsEditMode(false);
            fetchDriver();
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-steel/20 rounded-full flex items-center justify-center text-steel font-mono font-bold text-2xl">
                  {driver.firstName[0]}
                  {driver.lastName[0]}
                </div>
                <div>
                  <h1
                    className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                    style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
                  >
                    {driver.firstName} {driver.lastName}
                  </h1>
                  <p className="text-text-secondary text-sm font-mono mt-1">
                    {driver.driverNumber}
                    {driver.employeeId && ` | Employee: ${driver.employeeId}`}
                  </p>
                </div>
              </div>
              <Link
                to="/drivers"
                className="text-text-secondary text-sm font-mono hover:text-amber transition-colors mt-2 inline-block"
              >
                &larr; Back to Drivers
              </Link>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowStatusModal(true)}
                className="bg-steel/20 hover:bg-steel/30 text-steel font-mono py-2 px-4 transition-all uppercase text-sm tracking-wider border border-steel/30 hover:border-steel"
              >
                Update Status
              </button>
              <button
                onClick={() => setIsEditMode(true)}
                className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-2 px-6 transition-all uppercase tracking-wider"
                style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Status Badges */}
      <div className="flex items-center space-x-4">
        <span
          className={`${getStatusStyle(driver.status)} font-mono text-sm uppercase tracking-wider font-semibold px-4 py-2 rounded`}
        >
          {driver.status.replace('_', ' ')}
        </span>
        <span className={`${getEmploymentStyle(driver.employmentStatus)} font-mono text-sm`}>
          Employment: {driver.employmentStatus.replace('_', ' ')}
        </span>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Contact Information */}
        <DetailSection title="Contact Information">
          <DetailRow label="Phone" value={driver.phone || '-'} />
          <DetailRow label="Email" value={driver.email || '-'} />
          <DetailRow
            label="Address"
            value={
              driver.address
                ? `${driver.address}, ${driver.city || ''} ${driver.state || ''} ${driver.zip || ''}`
                : '-'
            }
          />
        </DetailSection>

        {/* Employment */}
        <DetailSection title="Employment">
          <DetailRow label="Hire Date" value={formatDate(driver.hireDate)} />
          <DetailRow label="Employment Status" value={driver.employmentStatus.replace('_', ' ')} />
          {driver.terminationDate && (
            <DetailRow label="Termination Date" value={formatDate(driver.terminationDate)} />
          )}
        </DetailSection>

        {/* License Information */}
        <DetailSection title="License Information">
          <DetailRow label="License Number" value={driver.licenseNumber || '-'} />
          <DetailRow label="License State" value={driver.licenseState || '-'} />
          <DetailRow label="License Class" value={driver.licenseClass || '-'} highlight />
          <DetailRow
            label="License Expiry"
            value={formatDate(driver.licenseExpiry)}
            warning={isDateWarning(driver.licenseExpiry)}
          />
        </DetailSection>

        {/* Certifications */}
        <DetailSection title="Certifications & Endorsements">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-text-tertiary font-mono text-sm uppercase tracking-widest">
                HAZMAT Certified
              </span>
              <span
                className={`font-mono text-sm ${driver.hazmatCertified ? 'text-success-green' : 'text-text-tertiary'}`}
              >
                {driver.hazmatCertified ? 'YES' : 'NO'}
              </span>
            </div>
            {driver.hazmatCertified && (
              <DetailRow
                label="HAZMAT Expiry"
                value={formatDate(driver.hazmatExpiry)}
                warning={isDateWarning(driver.hazmatExpiry)}
              />
            )}

            <div className="flex justify-between items-center pt-2">
              <span className="text-text-tertiary font-mono text-sm uppercase tracking-widest">
                Tanker Endorsement
              </span>
              <span
                className={`font-mono text-sm ${driver.tankerEndorsement ? 'text-success-green' : 'text-text-tertiary'}`}
              >
                {driver.tankerEndorsement ? 'YES' : 'NO'}
              </span>
            </div>

            <div className="flex justify-between items-center pt-2">
              <span className="text-text-tertiary font-mono text-sm uppercase tracking-widest">
                TWIC Card
              </span>
              <span
                className={`font-mono text-sm ${driver.twicCard ? 'text-success-green' : 'text-text-tertiary'}`}
              >
                {driver.twicCard ? 'YES' : 'NO'}
              </span>
            </div>
            {driver.twicCard && (
              <DetailRow
                label="TWIC Expiry"
                value={formatDate(driver.twicExpiry)}
                warning={isDateWarning(driver.twicExpiry)}
              />
            )}

            <DetailRow
              label="Medical Card Expiry"
              value={formatDate(driver.medicalCardExpiry)}
              warning={isDateWarning(driver.medicalCardExpiry)}
            />
          </div>
        </DetailSection>
      </div>

      {/* Terminal Credentials */}
      <div className="relative stat-card">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
          <DriverTerminalCredentials driverId={id!} />
        </div>
      </div>

      {/* Notes */}
      {driver.notes && (
        <DetailSection title="Notes">
          <p className="text-text-secondary font-mono text-sm whitespace-pre-wrap">{driver.notes}</p>
        </DetailSection>
      )}

      {/* Metadata */}
      <div className="text-text-tertiary font-mono text-xs space-y-1">
        <p>Created: {formatDate(driver.createdAt)}</p>
        <p>Updated: {formatDate(driver.updatedAt)}</p>
      </div>

      {/* Delete Button */}
      {driver.employmentStatus === 'ACTIVE' && (
        <div className="pt-4 border-t border-panel-border">
          <button
            onClick={() => setShowDeleteModal(true)}
            className="text-error-red hover:text-error-red/80 font-mono text-sm uppercase tracking-wider transition-colors"
          >
            Terminate Driver
          </button>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-background/80 flex items-center justify-center z-50">
          <div className="relative stat-card max-w-md w-full mx-4">
            <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
            <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

            <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
              <h3 className="text-xl font-display text-text-primary uppercase tracking-wide mb-6">
                Update Status
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    New Status
                  </label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="form-input w-full"
                  >
                    <option value="">-- Select Status --</option>
                    <option value="AVAILABLE">Available</option>
                    <option value="ON_DUTY">On Duty</option>
                    <option value="OFF_DUTY">Off Duty</option>
                    <option value="VACATION">Vacation</option>
                    <option value="SICK">Sick</option>
                    <option value="SUSPENDED">Suspended</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowStatusModal(false);
                    setNewStatus('');
                  }}
                  className="px-4 py-2 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStatusUpdate}
                  disabled={!newStatus}
                  className="px-4 py-2 bg-amber hover:bg-amber/90 text-background font-display font-bold uppercase tracking-wider transition-all disabled:opacity-50"
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-background/80 flex items-center justify-center z-50">
          <div className="relative stat-card max-w-md w-full mx-4">
            <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-error-red" />
            <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-error-red" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-error-red" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-error-red" />

            <div className="bg-panel-dark border border-error-red/50 p-6 clip-path-octagon">
              <h3 className="text-xl font-display text-error-red uppercase tracking-wide mb-4">
                Confirm Termination
              </h3>
              <p className="text-text-secondary font-mono text-sm mb-6">
                Are you sure you want to terminate driver{' '}
                <span className="text-text-primary">
                  {driver.firstName} {driver.lastName}
                </span>
                ? This will set their employment status to TERMINATED and mark them as inactive.
              </p>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-error-red hover:bg-error-red/90 text-white font-display font-bold uppercase tracking-wider transition-all"
                >
                  Terminate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <div className="flex items-center space-x-2 mb-4">
          <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
          <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">{title}</h3>
        </div>
        <div className="space-y-3">{children}</div>
      </div>
    </div>
  );
}

function DetailRow({
  label,
  value,
  highlight,
  warning,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  warning?: 'warning' | 'critical' | null;
}) {
  const valueClass = highlight
    ? 'text-amber font-semibold'
    : warning === 'critical'
      ? 'text-error-red'
      : warning === 'warning'
        ? 'text-warning-amber'
        : 'text-text-primary';

  return (
    <div className="flex justify-between items-center">
      <span className="text-text-tertiary font-mono text-sm uppercase tracking-widest">{label}</span>
      <span className={`font-mono text-sm ${valueClass}`}>
        {value}
        {warning === 'critical' && ' EXPIRED'}
        {warning === 'warning' && ' !'}
      </span>
    </div>
  );
}

export default DriverDetailPage;
